// src/components/maintenance/ProveedorModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Save, Building2, Phone, Zap } from 'lucide-react';
import { proveedorService } from '../../services/maintenance/proveedorService';

interface Proveedor {
    id?: number;
    codigo: string;
    razon_social: string;
    ruc: string;
    contacto: string;
    telefono: string;
    email: string;
    direccion: string;
    calificacion: 'A' | 'B' | 'C';
    dias_entrega_promedio: number;
    tiempo_respuesta_promedio_horas: number;
    especializacion: string[];
    activo: boolean;
}

interface ProveedorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    proveedor?: Proveedor | null;
}

const ESPECIALIZACIONES_DISPONIBLES = [
    'FLUIDOS',
    'FILTROS',
    'PARTES',
    'INSUMOS',
    'MANO_OBRA',
    'NEUMÁTICOS',
    'FRENOS',
    'SUSPENSIÓN',
    'ELÉCTRICO',
    'MOTOR'
];

const ProveedorModal: React.FC<ProveedorModalProps> = ({ isOpen, onClose, onSuccess, proveedor }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<Proveedor>({
        codigo: '',
        razon_social: '',
        ruc: '',
        contacto: '',
        telefono: '',
        email: '',
        direccion: '',
        calificacion: 'B',
        dias_entrega_promedio: 7,
        tiempo_respuesta_promedio_horas: 24,
        especializacion: [],
        activo: true
    });

    useEffect(() => {
        if (proveedor) {
            setFormData({
                ...proveedor,
                especializacion: proveedor.especializacion || []
            });
        } else {
            setFormData({
                codigo: '',
                razon_social: '',
                ruc: '',
                contacto: '',
                telefono: '',
                email: '',
                direccion: '',
                calificacion: 'B',
                dias_entrega_promedio: 7,
                tiempo_respuesta_promedio_horas: 24,
                especializacion: [],
                activo: true
            });
        }
    }, [proveedor, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (proveedor?.id) {
                await proveedorService.updateProveedor(proveedor.id, formData);
            } else {
                await proveedorService.createProveedor(formData);
            }
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Error guardando proveedor:', error);
            alert(error.response?.data?.message || 'Error al guardar proveedor');
        } finally {
            setLoading(false);
        }
    };

    const toggleEspecializacion = (esp: string) => {
        setFormData(prev => ({
            ...prev,
            especializacion: prev.especializacion.includes(esp)
                ? prev.especializacion.filter(e => e !== esp)
                : [...prev.especializacion, esp]
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-white/20">
                {/* Header Premium */}
                <div className="bg-slate-900 px-10 py-8 flex justify-between items-center relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                    <div className="relative z-10">
                        <h2 className="text-3xl font-black text-white tracking-tight leading-none mb-2">
                            {proveedor ? 'Editar Perfil' : 'Alta de Proveedor'}
                        </h2>
                        <p className="text-slate-400 text-xs font-black uppercase tracking-[0.2em]">
                            {proveedor ? `ID: ${proveedor.codigo}` : 'Registro de nuevo socio estratégico'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="bg-white/10 hover:bg-white/20 text-white rounded-2xl p-3 transition-all active:scale-95 relative z-10"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form Body with Custom Scrollbar */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar">
                    {/* Sección: Identidad Corporativa */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                <Building2 size={18} />
                            </div>
                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Identidad del Proveedor</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                            <div className="md:col-span-4">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Código de Registro <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.codigo}
                                    onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                                    className="w-full h-12 px-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold placeholder:text-slate-300"
                                    placeholder="PROV-000"
                                />
                            </div>

                            <div className="md:col-span-8">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Razón Social Completa <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.razon_social}
                                    onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                                    className="w-full h-12 px-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold"
                                    placeholder="Nombre oficial de la empresa"
                                />
                            </div>

                            <div className="md:col-span-6">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Documento ID (RUC)
                                </label>
                                <input
                                    type="text"
                                    value={formData.ruc}
                                    onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                                    className="w-full h-12 px-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold"
                                    placeholder="11 dígitos (obligatorio para facturación)"
                                    maxLength={11}
                                />
                            </div>

                            <div className="md:col-span-6">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Nivel de Confianza <span className="text-rose-500">*</span>
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {['A', 'B', 'C'].map((level) => (
                                        <button
                                            key={level}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, calificacion: level as any })}
                                            className={`h-12 rounded-2xl text-xs font-black transition-all border-2 ${formData.calificacion === level
                                                ? level === 'A' ? 'bg-emerald-50 border-emerald-500 text-emerald-700 shadow-lg shadow-emerald-100' :
                                                    level === 'B' ? 'bg-amber-50 border-amber-500 text-amber-700 shadow-lg shadow-amber-100' :
                                                        'bg-rose-50 border-rose-500 text-rose-700 shadow-lg shadow-rose-100'
                                                : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'
                                                }`}
                                        >
                                            CATEGORÍA {level}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sección: Canales de Comunicación */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                                <Phone size={18} />
                            </div>
                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Canales de Comunicación</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Gestor de Cuenta
                                </label>
                                <input
                                    type="text"
                                    value={formData.contacto}
                                    onChange={(e) => setFormData({ ...formData, contacto: e.target.value })}
                                    className="w-full h-12 px-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold"
                                    placeholder="Persona de contacto principal"
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Línea de Atención
                                </label>
                                <input
                                    type="tel"
                                    value={formData.telefono}
                                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                    className="w-full h-12 px-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold"
                                    placeholder="+51 000 000 000"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Correo Corporativo
                                </label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="w-full h-12 px-4 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold"
                                    placeholder="contacto@empresa.com"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                                    Sede Central / Almacén
                                </label>
                                <textarea
                                    value={formData.direccion}
                                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                                    className="w-full px-4 py-3 bg-slate-50 border-2 border-transparent focus:border-indigo-500/20 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-2xl text-sm font-bold resize-none"
                                    placeholder="Dirección fiscal o logística"
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección: Eficiencia & Especialidad */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                            <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                                <Zap size={18} />
                            </div>
                            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Eficiencia & Especialidad</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Eficiencia en Entregas</label>
                                <div className="flex items-center gap-4">
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.dias_entrega_promedio}
                                        onChange={(e) => setFormData({ ...formData, dias_entrega_promedio: parseInt(e.target.value) })}
                                        className="w-24 h-12 text-center bg-white border-2 border-slate-100 rounded-xl text-lg font-black text-slate-900 focus:border-indigo-500/20 transition-all"
                                    />
                                    <span className="text-xs font-black text-slate-500 uppercase">Días promedio de logística</span>
                                </div>
                            </div>

                            <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Velocidad de Respuesta</label>
                                <div className="flex items-center gap-4">
                                    <input
                                        type="number"
                                        min="1"
                                        value={formData.tiempo_respuesta_promedio_horas}
                                        onChange={(e) => setFormData({ ...formData, tiempo_respuesta_promedio_horas: parseInt(e.target.value) })}
                                        className="w-24 h-12 text-center bg-white border-2 border-slate-100 rounded-xl text-lg font-black text-slate-900 focus:border-indigo-500/20 transition-all"
                                    />
                                    <span className="text-xs font-black text-slate-500 uppercase">Horas para cotización</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 ml-1">
                                Áreas de Especialización Tecnica
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {ESPECIALIZACIONES_DISPONIBLES.map((esp) => {
                                    const isSelected = formData.especializacion.includes(esp);
                                    return (
                                        <button
                                            key={esp}
                                            type="button"
                                            onClick={() => toggleEspecializacion(esp)}
                                            className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all border-2 ${isSelected
                                                ? 'bg-slate-900 border-slate-900 text-white shadow-lg shadow-slate-200'
                                                : 'bg-white border-slate-100 text-slate-500 hover:border-slate-300'
                                                }`}
                                        >
                                            {esp.replace('_', ' ')}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Status & Guardado */}
                    <div className="flex flex-col sm:flex-row items-center gap-4 bg-indigo-50/50 p-6 rounded-[2.5rem] border border-indigo-100/50">
                        <div
                            onClick={() => setFormData({ ...formData, activo: !formData.activo })}
                            className="flex items-center gap-3 flex-1 cursor-pointer select-none"
                        >
                            <div className={`w-12 h-6 rounded-full relative transition-all duration-300 ${formData.activo ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 ${formData.activo ? 'left-7' : 'left-1'}`}></div>
                            </div>
                            <div>
                                <p className="text-sm font-black text-slate-900 uppercase tracking-tight leading-none mb-1">Estado Operativo</p>
                                <p className="text-[10px] font-bold text-slate-500 uppercase">{formData.activo ? 'Proveedor con acceso total' : 'Proveedor en modo pausa'}</p>
                            </div>
                        </div>

                        <div className="flex gap-3 w-full sm:w-auto">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-8 h-12 border-2 border-slate-200 text-slate-600 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center"
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="flex-1 sm:flex-none px-10 h-12 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 transition-all disabled:opacity-50 flex items-center justify-center gap-3 active:scale-95"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        {proveedor ? 'Confirmar Cambios' : 'Registrar Socio'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProveedorModal;
